import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/firebase-config'
import { useAuth } from './AuthContext'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setBudgetData } from '@/store/slices/budgetSlice'
import { setPortfolioData } from '@/store/slices/portfolioSlice'
import { setDebtData } from '@/store/slices/debtSlice'
import { setRealEstateData } from '@/store/slices/realEstateSlice'
import { setRetirementData } from '@/store/slices/retirementSlice'
import { setCashFlowAllocationData } from '@/store/slices/cashFlowAllocationSlice'

interface DataPersistenceContextType {
  isAuthenticated: boolean
  hasLoadedData: boolean
  saveData: (type: string) => Promise<{ success: boolean; error?: string }>
  loadData: () => Promise<void>
  forceReload: () => Promise<void>
}

const DataPersistenceContext = createContext<DataPersistenceContextType | null>(null)

export function useDataPersistence() {
  const context = useContext(DataPersistenceContext)
  if (!context) {
    throw new Error('useDataPersistence must be used within a DataPersistenceProvider')
  }
  return context
}

interface DataPersistenceProviderProps {
  children: ReactNode
}

export function DataPersistenceProvider({ children }: DataPersistenceProviderProps) {
  const { currentUser } = useAuth()
  const dispatch = useAppDispatch()
  const [hasLoadedData, setHasLoadedData] = useState(false)

  // Get current state for saving
  const budget = useAppSelector((state) => state.budget)
  const portfolio = useAppSelector((state) => state.portfolio)
  const debt = useAppSelector((state) => state.debt)
  const realEstate = useAppSelector((state) => state.realEstate)
  const retirement = useAppSelector((state) => state.retirement)
  const cashFlowAllocation = useAppSelector((state) => state.cashFlowAllocation)

  const isAuthenticated = !!currentUser

  const loadData = useCallback(async () => {
    if (!currentUser) {
      setHasLoadedData(false)
      return
    }

    try {
      const docRef = doc(db, 'users', currentUser.uid, 'financialData', 'main')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        
        if (data.budget) dispatch(setBudgetData(data.budget))
        if (data.portfolio) dispatch(setPortfolioData(data.portfolio))
        if (data.debt) dispatch(setDebtData(data.debt))
        if (data.realEstate) dispatch(setRealEstateData(data.realEstate))
        if (data.retirement) dispatch(setRetirementData(data.retirement))
        if (data.cashFlowAllocation) dispatch(setCashFlowAllocationData(data.cashFlowAllocation))
      }

      setHasLoadedData(true)
    } catch (error) {
      console.error('Error loading data:', error)
      setHasLoadedData(true) // Still set to true to prevent infinite loading
    }
  }, [currentUser, dispatch])

  const saveData = useCallback(async (type: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) {
      return { success: false, error: 'Not authenticated' }
    }

    try {
      const docRef = doc(db, 'users', currentUser.uid, 'financialData', 'main')
      
      const dataToSave: Record<string, unknown> = {
        updatedAt: new Date().toISOString(),
      }

      if (type === 'all' || type === 'budget') {
        dataToSave.budget = budget
      }
      if (type === 'all' || type === 'portfolio') {
        dataToSave.portfolio = portfolio
      }
      if (type === 'all' || type === 'debt') {
        dataToSave.debt = debt
      }
      if (type === 'all' || type === 'realEstate') {
        dataToSave.realEstate = realEstate
      }
      if (type === 'all' || type === 'retirement') {
        dataToSave.retirement = retirement
      }
      if (type === 'all' || type === 'cashFlowAllocation') {
        dataToSave.cashFlowAllocation = cashFlowAllocation
      }

      await setDoc(docRef, dataToSave, { merge: true })
      return { success: true }
    } catch (error) {
      console.error('Error saving data:', error)
      return { success: false, error: (error as Error).message }
    }
  }, [currentUser, budget, portfolio, debt, realEstate, retirement, cashFlowAllocation])

  const forceReload = useCallback(async () => {
    setHasLoadedData(false)
    await loadData()
  }, [loadData])

  useEffect(() => {
    if (currentUser && !hasLoadedData) {
      loadData()
    }
  }, [currentUser, hasLoadedData, loadData])

  const value: DataPersistenceContextType = {
    isAuthenticated,
    hasLoadedData,
    saveData,
    loadData,
    forceReload,
  }

  return (
    <DataPersistenceContext.Provider value={value}>
      {children}
    </DataPersistenceContext.Provider>
  )
}
